// Decorative SVG backdrop for anonymous surfaces (Landing, AuthLayout
// for /login, /enter, /forgot-password, /invite/:token). Pure
// decoration — aria-hidden and pointer-events-none — layered behind
// page content to reinforce the exam-paper/assessment theme.
//
// Layers, back-to-front:
//   1. Ruled exercise-book lines with a red left margin.
//   2. Corner motifs — graduation cap, MCQ bubble rows, pencil,
//      emerald brand tick, Listening waveform, Reading passage bars.
//   3. Scattered micro-ticks echoing the pass mark.
//
// Opacity note: kept in the 0.15–0.35 range so the art is legible
// against the brand-glow gradient behind it. Lower than that and it
// disappears; higher and it fights the page copy.
export function ExamBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden text-foreground"
    >
      {/* Ruled paper — full-bleed horizontal lines at 38px spacing. */}
      <svg
        className="absolute inset-0 h-full w-full opacity-[0.04] dark:opacity-[0.06]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="eb-rule"
            x="0"
            y="0"
            width="100%"
            height="38"
            patternUnits="userSpaceOnUse"
          >
            <line
              x1="0"
              y1="37.5"
              x2="100%"
              y2="37.5"
              stroke="currentColor"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#eb-rule)" />
        {/* Left-margin line like an exercise book. */}
        <line
          x1="72"
          y1="0"
          x2="72"
          y2="100%"
          stroke="hsl(var(--destructive) / 0.12)"
          strokeWidth="1"
        />
      </svg>

      {/* Top-left: graduation cap. */}
      <svg
        className="absolute left-[4%] top-[8%] h-16 w-16 opacity-[0.06] sm:h-24 sm:w-24"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4 22 L32 10 L60 22 L32 34 Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M16 28 V42 C16 46 22 50 32 50 C42 50 48 46 48 42 V28"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M58 22 V40"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="58" cy="44" r="2.5" fill="currentColor" />
      </svg>

      {/* Top-right: MCQ option bubbles (A / B / C / D). */}
      <svg
        className="absolute right-[5%] top-[12%] h-20 w-20 opacity-[0.07] sm:h-28 sm:w-28"
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g stroke="currentColor" strokeWidth="1.5">
          <circle cx="14" cy="18" r="6" />
          <circle cx="34" cy="18" r="6" />
          <circle cx="54" cy="18" r="6" fill="currentColor" />
          <circle cx="74" cy="18" r="6" />
        </g>
        <g stroke="currentColor" strokeWidth="1.5">
          <circle cx="14" cy="42" r="6" fill="currentColor" />
          <circle cx="34" cy="42" r="6" />
          <circle cx="54" cy="42" r="6" />
          <circle cx="74" cy="42" r="6" />
        </g>
        <g stroke="currentColor" strokeWidth="1.5">
          <circle cx="14" cy="66" r="6" />
          <circle cx="34" cy="66" r="6" />
          <circle cx="54" cy="66" r="6" />
          <circle cx="74" cy="66" r="6" fill="currentColor" />
        </g>
        <g stroke="currentColor" strokeWidth="1.5">
          <circle cx="14" cy="90" r="6" />
          <circle cx="34" cy="90" r="6" fill="currentColor" />
          <circle cx="54" cy="90" r="6" />
          <circle cx="74" cy="90" r="6" />
        </g>
      </svg>

      {/* Left-middle: pencil at an angle — writing cue. */}
      <svg
        className="absolute left-[3%] top-[48%] h-20 w-20 rotate-[25deg] opacity-[0.06] sm:h-28 sm:w-28"
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14 86 L22 78 L80 20 L88 28 L30 86 L22 94 Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M22 78 L30 86" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M72 28 L80 36"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M14 86 L12 94 L20 92 Z"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>

      {/* Right-middle: big emerald tick — the brand mark. */}
      <svg
        className="absolute right-[4%] top-[52%] h-24 w-24 opacity-[0.10] sm:h-36 sm:w-36"
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="50"
          cy="50"
          r="42"
          stroke="hsl(var(--success))"
          strokeWidth="2.5"
        />
        <path
          d="M30 52 L44 66 L72 36"
          stroke="hsl(var(--success))"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {/* Bottom-left: listening waveform. */}
      <svg
        className="absolute bottom-[10%] left-[7%] h-14 w-40 opacity-[0.07] sm:h-20 sm:w-56"
        viewBox="0 0 200 50"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="6" y1="25" x2="6" y2="25" />
          <line x1="16" y1="18" x2="16" y2="32" />
          <line x1="26" y1="10" x2="26" y2="40" />
          <line x1="36" y1="16" x2="36" y2="34" />
          <line x1="46" y1="6" x2="46" y2="44" />
          <line x1="56" y1="14" x2="56" y2="36" />
          <line x1="66" y1="20" x2="66" y2="30" />
          <line x1="76" y1="8" x2="76" y2="42" />
          <line x1="86" y1="18" x2="86" y2="32" />
          <line x1="96" y1="12" x2="96" y2="38" />
          <line x1="106" y1="22" x2="106" y2="28" />
          <line x1="116" y1="16" x2="116" y2="34" />
          <line x1="126" y1="10" x2="126" y2="40" />
          <line x1="136" y1="20" x2="136" y2="30" />
          <line x1="146" y1="14" x2="146" y2="36" />
          <line x1="156" y1="24" x2="156" y2="26" />
          <line x1="166" y1="18" x2="166" y2="32" />
          <line x1="176" y1="12" x2="176" y2="38" />
          <line x1="186" y1="22" x2="186" y2="28" />
          <line x1="196" y1="25" x2="196" y2="25" />
        </g>
      </svg>

      {/* Bottom-right: reading passage — stacked text bars. */}
      <svg
        className="absolute bottom-[8%] right-[6%] h-20 w-28 opacity-[0.06] sm:h-28 sm:w-40"
        viewBox="0 0 160 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g stroke="currentColor" strokeWidth="6" strokeLinecap="round">
          <line x1="10" y1="14" x2="150" y2="14" />
          <line x1="10" y1="34" x2="130" y2="34" />
          <line x1="10" y1="54" x2="142" y2="54" />
          <line x1="10" y1="74" x2="110" y2="74" />
          <line x1="10" y1="94" x2="138" y2="94" />
        </g>
      </svg>

      {/* Scattered micro-ticks. */}
      <svg
        className="absolute left-[38%] top-[22%] h-6 w-6 opacity-[0.10]"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5 13 L10 18 L20 6"
          stroke="hsl(var(--success))"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <svg
        className="absolute left-[60%] top-[80%] h-5 w-5 opacity-[0.10]"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5 13 L10 18 L20 6"
          stroke="hsl(var(--success))"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <svg
        className="absolute left-[82%] top-[38%] h-4 w-4 opacity-[0.10]"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5 13 L10 18 L20 6"
          stroke="hsl(var(--success))"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
